'use strict';
const _ = require('underscore');
const ContentFactory = require('data/domain/factory/content/index');
const ContentRepository = require('data/domain/repository/content/index');
const ContentWork = require('balus/domain/entity/work/content');
const WorkRepository = require('process/domain/repository/work/index');
const ActionWork = require('process/domain/entity/actionwork');
const ActionRepository = require('process/domain/repository/action/index');
const ActionWorkRepository = require('process/domain/repository/actionwork');
const ContentActionWorkAPI = require('balus/infra/api/actionwork/content');

class Repository extends ActionWorkRepository {
    /**
     * ActionWork を保存する。
     * 新規作成(id が null)の場合は create, 更新の場合は update する。
     * 内部的に Work を判別して自動的に対象の API を呼び出す
     * ※API のインターフェースは本来は ActionWork であるべきだが、
     * リファクタを段階的に行っているため、一旦今のインターフェースにする
     *
     * @param {Action} action
     * @param {Content} content
     * @param {object} contentJSON
     * @param {string} contentJSON.name
     * @param {string} contentJSON.uri
     * @param {string} relation
     */
    save(action, content, contentJSON, relation) {
        let promise;
        if(content) {
            promise = ContentActionWorkAPI.update(
                this._project.id, action.id, content.id, contentJSON
            );
        } else {
            promise = ContentActionWorkAPI.create(
                this._project.id, action.id, contentJSON
            );
        }

        return promise.then(actionWorkJSON => {
            const contentFactory = new ContentFactory(this._project);
            return contentFactory.build(actionWorkJSON.work.target)
                .then(_content => {
                    const content = ContentRepository.find(this._project)
                        .add(_content);

                    let work = new ContentWork({
                        id: actionWorkJSON.work.id,
                        action: action,
                        target: content
                    });
                    work = WorkRepository.find(this._project)
                        .add(work);

                    const actionWork = new ActionWork({
                        id: actionWorkJSON.id,
                        action: action,
                        work: work,
                        relation: relation
                    });

                    return this.add(actionWork);
                });
        });
    }

    updateRelation(actionWork, relation) {
        return ContentActionWorkAPI.updateRelation(this._project.id, actionWork.id, relation)
            .then(actionWorkJSON => {
                actionWork.set({relation: actionWorkJSON.relation});
            });
    }

    delete(actionWork) {
        return ContentActionWorkAPI.delete(this._project.id, actionWork.id)
            .then(() => {
                return this.remove(actionWork);
            });
    }

    addContents(action, contentIDs, direction) {
        return ContentActionWorkAPI.addContent(
            this._project.id, action.id,
            contentIDs
        ).then(actionWorksJSON => {
            _.each(actionWorksJSON, actionWorkJSON => {
                ContentRepository.find(this._project)
                    .fetchContentOnce(actionWorkJSON.work.target.id)
                    .then(content => {
                        let work = new ContentWork({
                            id: actionWorkJSON.work.id,
                            action: action,
                            target: content
                        });
                        work = WorkRepository.find(this._project)
                            .add(work);

                        const actionWork = new ActionWork({
                            id: actionWorkJSON.id,
                            action: action,
                            work: work,
                            direction: direction
                        });

                        return this.add(actionWork);
                    });
            });
        });
    }

    fetchActionWorksByAction(action, limit) {
        return ContentActionWorkAPI
            .getNext(this._project.id, action.id, limit)
            .then(actionWorksJSON => {
                const contentFactory = new ContentFactory(this._project);

                return Promise.all(
                    _.map(actionWorksJSON, j => {
                        return contentFactory.build(j.work.target)
                            .then(_content => {
                                const content = ContentRepository.find(this._project)
                                    .add(_content);

                                j.work.target = content;
                                const w = new ContentWork(j.work);
                                const work = WorkRepository.find(this._project)
                                    .add(w);

                                j.action = action;
                                j.work = work;
                                const aw = new ActionWork(j);
                                this.add(aw);
                            });
                    })
                );
            })
            .then(() => {
                return this.getActionWorksByAction(action);
            });
    }

    fetchActionWorksByActionOnce(action, limit) {
        let p = this._actionPromise[action.id];
        if(!p) {
            p = this.fetchActionWorksByAction(action, limit);
            this._actionPromise[action.id] = p;
        }
        return p;
    }

    hasNextActionWorksByAction(action) {
        return ContentActionWorkAPI.hasNext(this._project.id, action.id)
    }

    /**
     * アクションに関連付けられている ActionWork Content を全件取得する
     * @param action
     * @param perPage
     */
    fetchAllActionWorkContentsByAction(action, perPage = 20) {
        return new Promise(resolve => {
            this.fetchActionWorksByAction(action, perPage)
                .then(() => {
                    if (this.hasNextActionWorksByAction(action)) {
                        this.fetchAllActionWorkContentsByAction(action, perPage)
                            .then(() => {
                                resolve();
                            });
                    } else {
                        resolve();
                    }
                })
        });
    }

    fetchActionWorksByWork(content, limit) {
        let work;
        const actionRepo = ActionRepository.find(this._project);

        return ContentActionWorkAPI
            .getNextByContent(this._project.id, content.id, limit)
            .then(actionWorksJSON => {
                const contentFactory = new ContentFactory(this._project);

                return Promise.all(
                    _.map(actionWorksJSON, j => {
                        return contentFactory.build(j.work.target)
                            .then(_content => {
                                content = ContentRepository.find(this._project)
                                    .add(_content);
                                return actionRepo.get(j.action);
                            })
                            .then(action => {
                                j.work.target = content;
                                const w = new ContentWork(j.work);
                                work = WorkRepository.find(this._project)
                                    .add(w);

                                j.action = action;
                                j.work = work;
                                const aw = new ActionWork(j);
                                this.add(aw);
                            });
                    })
                );
            })
            .then(() => {
                if(!work) {
                    // content が work として登録されていない場合
                    return null;
                } else {
                    return this.getActionWorksByWork(work);
                }
            });
    }

    fetchActionWorksByWorkOnce(content, limit) {
        let p = this._workPromise[content.id];
        if(!p) {
            p = this.fetchActionWorksByWork(content, limit);
            this._workPromise[content.id] = p;
        }
        return p;
    }

    static find(project) {
        const key = project.id;
        let r = repositories[key];
        if (!r) {
            r = new Repository(project);
            repositories[key] = r;
        }
        return r;
    }
}

const repositories = {};

module.exports = Repository;



// WEBPACK FOOTER //
// ./balus/infra/repository/actionwork/content.js